import { action, computed, IReactionDisposer, observable, reaction, makeObservable } from "mobx";
import RouterStore from "../../store/RouterStore";
import { Estrutura } from "../modelo/Estrutura";
import { MinutaGabineteGeral } from "../modelo/MinutaGabineteEdicao";
import { NoArvore } from "../modelo/NoArvore";
import { ParteAM } from "../modelo/ParteAM";
import { TemaAM } from "../modelo/TemaAM";

class MinutaEdicaoStore {
    minuta: MinutaGabineteGeral;
    indexTemaEmEdicao: number;
    idTemaEmEdicao: number;
    pseudoIdTemaEmEdicao: string;
    parteDoTemaEmEdicao: ParteAM;
    abaSelecionada: number = 0;
    abaSelecionadaDaEstrutura: number = 0;
    abaSelecionadaDoTema: number = 0;
    estruturaEdicao: Estrutura;
    temasParaAdicionarParte: TemaAM[] = [];
    recursoDoTemaEmEdicao: string = '';
    salvamentoEmAndamento: boolean = false;
    alteracoes: number = 0;
    alteracaoArvoreTemas: boolean = false;
    idProcesso: number;
    sucessoAoSalvar: boolean;

    constructor() {
        makeObservable(this, {
            minuta: observable,
            indexTemaEmEdicao: observable,
            idTemaEmEdicao: observable,
            pseudoIdTemaEmEdicao: observable,
            parteDoTemaEmEdicao: observable,
            abaSelecionada: observable,
            abaSelecionadaDaEstrutura: observable,
            abaSelecionadaDoTema: observable,
            estruturaEdicao: observable,
            temasParaAdicionarParte: observable,
            recursoDoTemaEmEdicao: observable,
            salvamentoEmAndamento: observable,
            alteracoes: observable,
            alteracaoArvoreTemas: observable,
            idProcesso: observable,
            sucessoAoSalvar: observable,
            salvando: action,
            idMinuta: computed,
            emEdicao: computed,
            isAbaEdicaoTemaSelecionada: computed,
            temaEmEdicao: computed,
            limparAlteracoes: action,
            atualizarIndexMinutaEmEdicao: action,
            limparStore: action,
            arvoreRetirandoRecursosSemTema: computed,
            haRecursosSemTema: computed
        });

        // As reações precisam ser criadas depois do makeObservable. Como campos de classe elas
        // eram instanciadas antes, quando 'pseudoIdTemaEmEdicao' e 'minuta' ainda eram valores
        // comuns: não observavam nada e nunca disparavam, deixando 'indexTemaEmEdicao' sempre
        // indefinido e, por consequência, 'temaEmEdicao' sem resolver o tema.
        this.reactionIdEditarTema = reaction(() => this.pseudoIdTemaEmEdicao, _idTema => {
            this.atualizarIndexMinutaEmEdicao();
        });

        this.reactionArvoreEditarTema = reaction(() => this.minuta?.arvoreTemas, _arvore => {
            this.atualizarIndexMinutaEmEdicao();
        });
    }

    salvando(estaEmAndamento: boolean) {
        this.salvamentoEmAndamento = estaEmAndamento;
    }


    get numProcesso() {
        return Buffer.from(RouterStore.params.numProcesso, 'base64').toString();
    }

    get tipoMinuta(): number {
        return parseInt(RouterStore.params.tipoMinuta);
    }

    get idMinuta() {
        return RouterStore.params.idMinuta;
    }

    get estaSalvando() {
        return this.salvamentoEmAndamento;
    }

    get emEdicao() {
        return this.minuta.minuta;
    }

    get isAbaEdicaoTemaSelecionada() {
        return this.abaSelecionada === 1;
    }

    get temaEmEdicao(): TemaAM {
        return this.minuta?.arvoreTemas[this.indexTemaEmEdicao]?.minutaTema;
    }

    arvoreTemas = (parte: ParteAM, recurso: string): NoArvore[] => {
        return this.minuta.arvoreTemas.filter((no) => {
            return no.codigoParte === parte.codigo && no.tipoParte === parte.tipo && no.siglaClasse === recurso;
        });
    }

    limparAlteracoes = (sucessoAoSalvar: boolean) => {
        this.alteracoes = 0;
        this.alteracaoArvoreTemas = false;
        this.sucessoAoSalvar = sucessoAoSalvar;
        this.salvando(false);
    };

    reactionIdEditarTema: IReactionDisposer;
    reactionArvoreEditarTema: IReactionDisposer;

    atualizarIndexMinutaEmEdicao = () => {
        this.indexTemaEmEdicao = this.minuta?.arvoreTemas?.findIndex((no) => {
            return no.codigoParte === this.parteDoTemaEmEdicao?.codigo
                && no.siglaClasse === this.recursoDoTemaEmEdicao
                && no.minutaTema.id === this.idTemaEmEdicao;
        });
    };

    limparStore = () => {
        this.abaSelecionada = 0;
        this.abaSelecionadaDaEstrutura = 0;
        this.abaSelecionadaDoTema = 0;
        this.minuta = undefined;
        this.indexTemaEmEdicao = undefined;
        this.parteDoTemaEmEdicao = undefined;
        this.estruturaEdicao = undefined;
        this.temasParaAdicionarParte = [];
        this.recursoDoTemaEmEdicao = '';
        this.limparAlteracoes(undefined);
        this.salvando(false);
    };

    get arvoreRetirandoRecursosSemTema() {
        const arvoreCalculada = this.minuta?.arvoreTemas.filter((no) => {
            return no.minutaTema !== undefined;
        }) || [];
        return JSON.stringify(arvoreCalculada);
    }

    get haRecursosSemTema() {
        return ((JSON.stringify(this.minuta?.arvoreTemas) || []) !==
            this.arvoreRetirandoRecursosSemTema);
    }
}

export default new MinutaEdicaoStore();
